import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from 'react';
import { api } from './api';

export interface User {
  id: string;
  username: string;
  displayName: string;
  role: 'admin' | 'employee';
  /** When the Create lot tour was finished or skipped; null = start it. */
  tourDoneAt: string | null;
  /** May use Receiving (pilot); always true for administrators. */
  canReceive: boolean;
}

interface AuthState {
  user: User | null;
  loading: boolean;
  refresh: () => Promise<void>;
  logout: () => Promise<void>;
}

const Ctx = createContext<AuthState>(null as unknown as AuthState);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const r = await api<{ user: User | null }>('/auth/me');
      setUser(r.user);
    } catch {
      /* keep the current state if the server is unreachable */
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
    const onUnauthorized = () => setUser(null);
    window.addEventListener('mal:unauthorized', onUnauthorized);
    return () => window.removeEventListener('mal:unauthorized', onUnauthorized);
  }, [refresh]);

  const logout = useCallback(async () => {
    await api('/auth/logout', { method: 'POST' }).catch(() => undefined);
    setUser(null);
  }, []);

  return <Ctx.Provider value={{ user, loading, refresh, logout }}>{children}</Ctx.Provider>;
}

export const useAuth = () => useContext(Ctx);
